"use client";

import Link from "next/link";
import AgentHeroAnimation from "./AgentHeroAnimation";
import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowUpRight, PlayCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUser } from "@clerk/nextjs";
import HeroResumeUpload from "./HeroResumeUpload";
import styles from "./Hero.module.css";

export const Hero = () => {
  const { t, i18n } = useTranslation();
  const { isSignedIn } = useUser();
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const isSpanish = i18n.language?.startsWith("es");
  const titleLeading = t("HOME_TITLE_LEADING_TEXT");
  const titleHighlight = t("HOME_TITLE_HIGHLIGHT_TEXT");
  const highlightWords = titleHighlight.trim().split(/\s+/);
  const highlightPrefix = isSpanish ? `${highlightWords[0]} ` : "";
  const highlightEmphasis = isSpanish
    ? highlightWords.slice(1).join(" ")
    : titleHighlight;

  const handleOpenDemo = () => setIsDemoOpen(true);

  return (
    <>
      <section className={`${styles.hero} relative isolate -mx-3 -mt-12 overflow-hidden rounded-[1.75rem] px-5 pt-12 sm:-mx-4 sm:rounded-[2rem] sm:px-8 sm:pt-16 lg:-mx-8 lg:px-12`}>
        <div className="relative flex flex-col items-center gap-10 sm:gap-12">
          <div className="w-full min-w-0 max-w-5xl space-y-6 text-center [font-family:var(--font-body)] lg:space-y-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="inline-flex items-center"
            >
              <Badge className="rounded-full border border-white/65 bg-white/45 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700 shadow-none backdrop-blur-sm hover:bg-white/60 dark:border-white/25 dark:bg-white/10 dark:text-white dark:hover:bg-white/10">
                {t("HOME_DESC_TEXT")}
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="mx-auto max-w-[58rem] text-balance text-[clamp(2.2rem,8vw,3.75rem)] font-bold leading-[1.08] tracking-[-0.045em] text-slate-900 dark:text-white sm:text-[clamp(3.25rem,5.2vw,5rem)]"
            >
              <span className="block">{titleLeading}</span>{" "}
              <span className="block">
                {highlightPrefix}
                <span className="bg-gradient-to-r from-[#254b96] via-[#4458a1] to-[#216e6d] bg-clip-text text-transparent dark:from-sky-100 dark:via-white dark:to-emerald-100">
                  {highlightEmphasis}
                </span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.7 }}
              className="mx-auto max-w-[38rem] text-balance text-center [direction:ltr] text-lg font-medium leading-relaxed tracking-[-0.01em] text-slate-700 dark:text-blue-100 sm:text-xl lg:text-[1.375rem]"
            >
              {t("HOME_SUBTITLE_TEXT")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.36, duration: 0.7 }}
              className="flex flex-row flex-wrap items-center justify-center gap-1.5 sm:flex-nowrap sm:gap-5"
            >
              <Link
                href={isSignedIn ? "/dashboard" : "/sign-up"}
                className="group inline-flex min-h-11 w-[9.25rem] min-w-0 items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 px-3.5 py-2.5 text-white shadow-lg shadow-slate-950/20 transition hover:opacity-95 sm:min-h-0 sm:w-auto sm:flex-none sm:gap-1.5 sm:px-4 sm:py-2"
              >
                <span className="truncate whitespace-nowrap text-[13px] font-semibold leading-none sm:hidden">
                  {isSignedIn ? t("NAV_OPEN_APP_LABEL") : t("HOME_CTA_STARTFREE_BUTTON")}
                </span>
                <span className="hidden text-sm font-semibold leading-none sm:inline">
                  {isSignedIn ? t("NAV_OPEN_APP_LABEL") : t("HOME_HERO_CTA_TEXT")}
                </span>
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20 text-white transition group-hover:scale-105 sm:h-6 sm:w-6">
                  <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                </span>
              </Link>

              <button
                onClick={handleOpenDemo}
                aria-expanded={isDemoOpen}
                aria-haspopup="dialog"
                className="inline-flex min-h-11 w-[9.25rem] min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-0.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700/70 dark:text-white dark:hover:bg-white/10 dark:focus-visible:ring-white/70 sm:min-h-10 sm:w-auto sm:gap-2.5 sm:px-2.5 sm:text-sm"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/40 text-[#254b96] dark:bg-white/10 dark:text-white sm:h-8 sm:w-8">
                  <PlayCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </span>
                <span className="sm:hidden">{t("HOME_DEMO_MOBILE_TEXT")}</span>
                <span className="hidden sm:inline">{t("HOME_DEMO_TEXT")}</span>
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32, duration: 0.7 }}
            >
              <div className={styles.resumeUpload}>
                <HeroResumeUpload />
              </div>
            </motion.div>

          </div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="relative w-full min-w-0 max-w-[1000px] rounded-t-[1.75rem] border border-b-0 border-white/60 bg-white/25 p-1.5 pb-0 shadow-[0_-12px_60px_-24px_rgba(9,23,55,0.3)] backdrop-blur-sm dark:border-white/25 dark:bg-white/10 sm:p-2 sm:pb-0"
          >
            <div className="relative isolate h-[260px] w-full overflow-hidden rounded-t-[26px] [mask-image:linear-gradient(to_bottom,black_0%,black_92%,transparent_100%)] sm:h-[320px] lg:h-[360px]">
              <div
                className="pointer-events-none absolute -inset-3 -z-10 bg-[url('/assets/agent-hero-background.jpg')] bg-cover bg-center bg-no-repeat blur-xl dark:hidden sm:-inset-4"
                aria-hidden
              />
              <AgentHeroAnimation />
            </div>
          </motion.div>
        </div>
      </section>
      <Dialog open={isDemoOpen} onOpenChange={setIsDemoOpen}>
        <DialogContent className="max-h-[92dvh] w-[calc(100vw-1.5rem)] max-w-5xl gap-0 overflow-y-auto rounded-2xl border border-white/15 bg-slate-950 p-0 text-white shadow-2xl shadow-slate-950/50 sm:rounded-3xl [&>button]:right-4 [&>button]:top-4 [&>button]:z-10 [&>button]:rounded-full [&>button]:bg-white/10 [&>button]:p-2 [&>button]:text-white [&>button]:opacity-100 [&>button]:transition-colors [&>button]:hover:bg-white/20 [&>button]:hover:text-white [&>button]:focus:ring-white/70">
          <DialogHeader className="px-5 pb-4 pt-5 text-left sm:px-8 sm:pb-5 sm:pt-7">
            <DialogTitle className="pr-12 text-xl font-semibold tracking-tight text-white sm:text-2xl">
              {t("HOME_DEMO_TITLE_TEXT")}
            </DialogTitle>
            <DialogDescription className="max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
              {t("HOME_DEMO_SUBHEADING_TEXT")}
            </DialogDescription>
          </DialogHeader>
          <div className="aspect-video w-full overflow-hidden bg-black">
            <video
              src="/assets/oficial-demo-landfast.mp4"
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="h-full w-full"
            />
          </div>
          <p className="px-5 py-4 text-xs text-slate-400 sm:px-8 sm:py-5 sm:text-sm">
            {t("HOME_DEMO_TIP_TEXT")}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
